import React, { useState, useCallback, useEffect } from 'react';
import { formatISK } from '@/utils/formatISK';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { dashboardAPI, onboardingAPI, timeEntriesAPI, projectsAPI, reportsAPI, companyAPI } from '@/services/api';
import { useCachedData } from '@/hooks/useCachedData';
import CreateFirstProject from '@/components/CreateFirstProject';
import PWAInstallFlow from '@/components/PWAInstallFlow';
import InviteTeamCard from '@/components/InviteTeamCard';
import FirstStepsCard from '@/components/FirstStepsCard';
import { usePlan } from '@/contexts/PlanContext';
import InvoiceProjectPicker from '@/components/InvoiceProjectPicker';
import InvoiceEditorModal from '@/components/InvoiceEditorModal';
import { useIntegrationsAccess } from '@/hooks/useIntegrationsAccess';
import { 
  Camera, Clock, FolderOpen, AlertTriangle, FileText,
  ChevronRight, ChevronLeft, ArrowRight, Receipt, Users, Link2Off, RefreshCw, Info, CalendarDays, Briefcase
} from 'lucide-react';
import InfoTooltip from '@/components/InfoTooltip';
import { SkeletonDashboard } from '@/components/Skeletons';
import MonthPicker from '@/components/MonthPicker';
import DailyWorkLauncher from '@/components/DailyWorkLauncher';
import MyJobsSection from '@/components/MyJobsSection';
import { formatHoursNum, formatKlst } from '@/utils/formatHours';

const ISK = (n) => formatISK(Math.round(n || 0));

/** Lightweight hook that fetches unbilled totals for the whole company. */
const useUnbilledTotal = (enabled = true) => {
  // Both figures come from the SERVER — the frontend used to compute
  // `withVat = Math.round(total * 1.24)` client-side, which was
  // actively wrong for any tenant billing at 0% (labor with VAT-
  // refund) or 11% (matvara/gisting): the "með VSK" number simply
  // wasn't reality. Now the server rolls up real per-piece VAT
  // (per-receipt rate + per-material rate) into an authoritative
  // `unbilled_total_with_vat` and the tile just displays it.
  const [totals, setTotals] = useState({ ex: null, withVat: null });
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!enabled) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    (async () => {
      try {
        // A bounded request: the card must never sit on a grey placeholder
        // forever if the answer is slow or the connection drops.
        const res = await companyAPI.getUnbilledSummary({ timeout: 25000 });
        if (!cancelled) {
          const t = res.data?.totals || {};
          setTotals({
            ex: t.unbilled_total || 0,
            withVat: t.unbilled_total_with_vat ?? null,
          });
        }
      } catch {
        if (!cancelled) { setTotals({ ex: null, withVat: null }); setFailed(true); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [enabled, attempt]);
  return {
    total: totals.ex, totalWithVat: totals.withVat, loading, failed,
    retry: () => setAttempt((n) => n + 1),
  };
};

/** Small badge showing unbilled totals (án VSK + með VSK) for the Ready-to-Bill card. */
const UnbilledTotalBadge = ({ lang }) => {
  const { total, totalWithVat, loading, failed, retry } = useUnbilledTotal(true);
  if (loading) {
    return (
      <span className="inline-block h-4 w-24 bg-card-hover rounded animate-pulse" data-testid="unbilled-total-loading" />
    );
  }
  if (failed || total === null) {
    return (
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); retry(); }}
        className="text-xs font-medium text-accent hover:underline"
        data-testid="unbilled-total-retry"
      >
        {lang === 'is' ? 'Sækja upphæð' : 'Load amount'}
      </button>
    );
  }
  const isIs = lang === 'is';
  if (total <= 0) {
    return (
      <span className="text-xs font-medium text-emerald-400" data-testid="unbilled-total-empty">
        {isIs ? 'Allt reikningsfært' : 'All caught up'}
      </span>
    );
  }
  // Server-authoritative "með VSK" from `unbilled_total_with_vat` — the
  // client never applies its own VAT rate anymore. Fall back to the
  // old `total * 1.24` only if the field is genuinely missing (should
  // not happen post-Phase-4; kept purely as a defensive display so a
  // future backend deploy that drops the field doesn't blank the tile).
  const withVat = (totalWithVat != null) ? totalWithVat : Math.round(total * 1.24);
  return (
    <div className="flex flex-col leading-tight" data-testid="unbilled-total-badge">
      <span className="text-sm font-bold font-mono text-amber-400" data-testid="unbilled-total-ex-vat">
        {ISK(total)} kr
        <span className="text-[10px] font-medium text-foreground-muted ml-1">{isIs ? 'án VSK' : 'ex VAT'}</span>
      </span>
      <span className="text-[11px] font-mono text-foreground-muted" data-testid="unbilled-total-inc-vat">
        {ISK(withVat)} kr
        <span className="text-[10px] ml-1">{isIs ? 'með VSK' : 'inc VAT'}</span>
      </span>
    </div>
  );
};

/** VSK yfirlit card - shows deductible VAT summary for selected period */
const VatSummaryCard = ({ selectedMonth }) => {
  const { t } = useI18n();
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVat = async () => {
      setLoading(true);
      try {
        let from, to;
        const [sy, sm] = selectedMonth ? selectedMonth.split('-').map(Number) : [new Date().getFullYear(), new Date().getMonth() + 1];
        if (period === 'month') {
          from = `${sy}-${String(sm).padStart(2, '0')}-01`;
          const lastDay = new Date(sy, sm, 0).getDate();
          to = `${sy}-${String(sm).padStart(2, '0')}-${lastDay}`;
        } else {
          // 2 months = selected month + previous month
          const prevMo = sm === 1 ? 12 : sm - 1;
          const prevYr = sm === 1 ? sy - 1 : sy;
          from = `${prevYr}-${String(prevMo).padStart(2, '0')}-01`;
          const lastDay = new Date(sy, sm, 0).getDate();
          to = `${sy}-${String(sm).padStart(2, '0')}-${lastDay}`;
        }
        const res = await reportsAPI.vatSummary({ date_from: from, date_to: to });
        setData(res.data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchVat();
  }, [period, selectedMonth]);

  if (loading) {
    return (
      <div className="card p-3.5 animate-pulse">
        <div className="h-3 w-28 bg-card-hover rounded mb-3" />
        <div className="h-5 w-20 bg-card-hover rounded" />
      </div>
    );
  }
  if (!data || (data.quality?.total_receipts || 0) === 0) return null;

  const atRisk = data.vat_at_risk_isk || 0;
  const needsReview = (data.quality?.needs_review_count || 0) + (data.quality?.estimated_count || 0);

  return (
    <section data-testid="vat-summary-card">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs uppercase tracking-wide text-foreground-muted">
          <Receipt size={14} className="inline mr-1.5 -mt-0.5" />
          {t('vat_summary_title')}
          <InfoTooltip text={t('vat_summary_tooltip')} className="ml-1" />
        </h2>
        <div className="flex gap-0.5 bg-card rounded-md p-0.5 border border-border" data-testid="vat-period-toggle">
          <button onClick={() => setPeriod('month')} className={`px-2 py-0.5 text-[11px] font-medium rounded transition-colors ${period === 'month' ? 'bg-accent text-background' : 'text-foreground-muted hover:text-foreground'}`} data-testid="vat-period-month">
            {t('vat_period_month')}
          </button>
          <button onClick={() => setPeriod('bimonthly')} className={`px-2 py-0.5 text-[11px] font-medium rounded transition-colors ${period === 'bimonthly' ? 'bg-accent text-background' : 'text-foreground-muted hover:text-foreground'}`} data-testid="vat-period-bimonthly">
            {t('vat_period_bimonthly')}
          </button>
        </div>
      </div>
      <div className="card p-3.5 space-y-2.5">
        {/* Rate breakdown */}
        <div className="space-y-1">
          {data.by_rate?.map((r, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-foreground-muted">{t('vat_label')} {Math.round(r.vat_rate * 100)}%</span>
              <span className="font-mono">{ISK(r.vat_isk)} {t('kr')} <span className="text-foreground-muted text-[10px]">({r.receipt_count} {r.receipt_count === 1 ? t('receipts_unit_one') : t('receipts_unit_many')})</span></span>
            </div>
          ))}
        </div>
        {/* Totals */}
        <div className="border-t border-border pt-2 flex justify-between items-center">
          <span className="font-semibold text-xs">{t('vat_total')}</span>
          <span className="font-mono font-bold text-sm text-accent" data-testid="vat-total">{ISK(data.totals?.vat_isk)} {t('kr')}</span>
        </div>
        {/* At risk */}
        {atRisk > 0 && (
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-2 flex items-start gap-2" data-testid="vat-at-risk">
            <Info size={12} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[11px] font-medium text-amber-400">{t('vat_at_risk')}: {ISK(atRisk)} {t('kr')}</p>
              <p className="text-[10px] text-foreground-muted">{needsReview} {t('vat_at_risk_detail')}</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const planCtx = usePlan();
  const { allowed: integrationsAllowed } = useIntegrationsAccess();
  const createInvoiceLabel = lang === 'is'
    ? (integrationsAllowed ? 'Búa til reikning' : 'Ný reikningsfærsla')
    : (integrationsAllowed ? 'Create invoice' : 'New billing entry');
  const [searchParams, setSearchParams] = useSearchParams();
  const [teamView, setTeamView] = useState('today');
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editorProjectId, setEditorProjectId] = useState(null);

  // Show upgrade success toast if redirected from checkout
  useEffect(() => {
    if (searchParams.get('upgraded') === 'true') {
      const { toast } = require('sonner');
      toast.success(t('subscription_activated_toast'));
      setSearchParams({}, { replace: true });
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Month selector state
  const now = new Date();
  const [selYear, setSelYear] = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  const selectedMonthKey = `${selYear}-${String(selMonth).padStart(2, '0')}`;
  const monthNames = t('month_names');
  const isCurrentMonth = selYear === now.getFullYear() && selMonth === now.getMonth() + 1;

  const prevMonth = () => {
    if (selMonth === 1) { setSelMonth(12); setSelYear(y => y - 1); }
    else setSelMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (isCurrentMonth) return;
    if (selMonth === 12) { setSelMonth(1); setSelYear(y => y + 1); }
    else setSelMonth(m => m + 1);
  };

  const isWorker = user?.role === 'worker';
  const isBookkeeper = user?.role === 'bookkeeper';
  const isManager = user?.role === 'manager';
  const isFullFinancial = ['owner', 'admin', 'bookkeeper'].includes(user?.role);

  const fetchAll = useCallback(async () => {
    const [dashboardRes, onboardingRes, projectsRes] = await Promise.all([
      dashboardAPI.get(selectedMonthKey),
      onboardingAPI.getStatus(),
      projectsAPI.list(true),
    ]);

    const status = onboardingRes.data;

    let workerData = null;
    if (isWorker) {
      const today = new Date().toISOString().split('T')[0];
      const now = new Date();
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      const weekStart = new Date(now.setDate(diff)).toISOString().split('T')[0];

      try {
        const [todayEntries, weekEntries] = await Promise.all([
          timeEntriesAPI.list(today),
          timeEntriesAPI.list(weekStart)
        ]);
        const todayData = todayEntries.data?.entries || todayEntries.data || [];
        const weekData = weekEntries.data?.entries || weekEntries.data || [];
        workerData = {
          hoursToday: todayData.reduce((sum, e) => sum + (e.hours || 0), 0),
          hoursThisWeek: weekData.reduce((sum, e) => sum + (e.hours || 0), 0),
        };
      } catch {
        workerData = { hoursToday: 0, hoursThisWeek: 0 };
      }
    }

    return { dashboard: dashboardRes.data, workerData, projectCount: (projectsRes.data || []).length, receiptCount: status.counts?.receipts || 0, activityCounts: status.counts || {}, firstProjectId: (projectsRes.data || [])[0]?.project_id || null };
  }, [isWorker, selectedMonthKey]);

  const { data: cached, loading, error: fetchError, refresh } = useCachedData(`dashboard-${selectedMonthKey}`, fetchAll, [selectedMonthKey]);

  const data = cached?.dashboard || null;
  const workerData = cached?.workerData || null;
  const projectCount = cached?.projectCount ?? null;
  const receiptCount = cached?.receiptCount ?? 0;
  const activityCounts = cached?.activityCounts || {};
  const navigate = useNavigate();

  // Redirect first-time users to onboarding
  const onboardingKey = user?.user_id ? `rektra_onboarding_${user.user_id}` : 'rektra_onboarding_complete';
  const needsOnboarding = !loading && data !== null && projectCount === 0 && receiptCount === 0
    && (() => { try { return localStorage.getItem(onboardingKey) !== 'true'; } catch { return false; } })();
  useEffect(() => {
    if (needsOnboarding) navigate('/welcome', { replace: true });
  }, [needsOnboarding, navigate]);

  // Data fetch error with no cached data
  if (fetchError && !data) {
    return (
      <div className="page-content flex flex-col items-center justify-center min-h-[60vh] gap-4" data-testid="dashboard-error">
        <AlertTriangle size={40} className="text-warning" />
        <p className="text-foreground-muted text-center">{t('dashboard.error_load')}</p>
        <button onClick={refresh} className="btn btn-primary btn-sm" data-testid="dashboard-retry-btn">
          <RefreshCw size={16} /> {t('retry') || 'Reyna aftur'}
        </button>
      </div>
    );
  }

  // Never paint the real homepage before we know whether onboarding is
  // needed: `data === null` means the first fetch has not answered yet, and
  // that one frame was the flash new signups saw (iter366).
  if (loading || data === null || needsOnboarding) {
    return <SkeletonDashboard />;
  }


  // Zero-project funnel: only show when API has confirmed zero projects (not from stale cache)
  // Also check that the dashboard API has returned data — prevents false positives
  // when the projects API fails temporarily (network issue, 502, etc.)
  if (projectCount === 0 && data !== null && !loading && receiptCount === 0) {
    return (
      <CreateFirstProject
        headline={t('no_projects_yet')}
        subtext={t('create_first_project')}
      />
    );
  }

  const firstName = user?.name?.split(' ')[0] || '';
  const currentHour = new Date().getHours();
  let greeting = t('greeting_evening');
  if (currentHour < 12) greeting = t('greeting_morning');
  else if (currentHour < 18) greeting = t('greeting_afternoon');

  const getFormattedDate = () => {
    const now = new Date();
    const day = now.getDate();
    const dayNames = t('day_names');
    const mNames = t('month_names');
    const month = mNames[now.getMonth()];
    const weekday = dayNames[now.getDay()];
    return `${day}. ${month}, ${weekday}`;
  };

  const formatCurrency = (amount) => {
    return formatISK(Math.round(amount || 0));
  };

  const thisMonth = data?.this_month || {};
  const attention = data?.attention || {};
  const teamHours = data?.team_hours || [];

  const hasAttentionItems = 
    (attention.unassigned_receipts || 0) > 0 ||
    (attention.pending_receipts || 0) > 0 ||
    (attention.projects_missing_hours?.length || 0) > 0;
  
  const hasNoData = (thisMonth.total_hours || 0) === 0 && (thisMonth.allur_kostnadur || 0) === 0 && (thisMonth.rekstrarkostnadur_net || 0) === 0;
  const showOnboardingGuide = hasNoData && isCurrentMonth;

  const teamToggleItems = [
    { key: 'today', label: t('team_today') },
    { key: 'week', label: t('team_this_week') },
    { key: 'month', label: t('team_this_month') },
  ];

  // ========== WORKER DASHBOARD (Tier 3: personal) ==========
  if (isWorker) {
    return (
      <>
        <div className="space-y-8" data-testid="dashboard-worker">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                {greeting}, {firstName}
              </h1>
              <p className="text-foreground-muted text-sm mt-1">{getFormattedDate()}</p>
            </div>
            <button onClick={refresh} className="p-2 text-foreground-muted hover:text-foreground rounded-lg transition-colors" data-testid="refresh-btn" title={t('refresh')}>
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>

          {/* 1. Dagsverkefni — work launcher */}
          <DailyWorkLauncher />

          {/* 2. Mín verkefni — task visibility */}
          <MyJobsSection />

          {/* PWA Install suggestion */}
          <PWAInstallFlow counts={activityCounts} />

          {/* 2. Flýtiaðgerðir */}
          <section data-testid="worker-actions">
            <h2 className="text-xs uppercase tracking-wide text-foreground-muted mb-4">
              {t('quick_actions')}
            </h2>
            <div className="space-y-3">
              <Link to="/time" className="flex items-center gap-4 p-5 card card-interactive" data-testid="action-log-hours">
                <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
                  <Clock size={24} className="text-background" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold">{t('log_hours')}</p>
                  <p className="text-sm text-foreground-muted">{t('log_work_hours')}</p>
                </div>
                <ChevronRight size={20} className="text-foreground-muted" />
              </Link>
              <Link to="/receipts/new" className="flex items-center gap-4 p-5 card card-interactive" data-testid="action-scan-receipt">
                <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
                  <Camera size={24} className="text-background" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold">{t('scan_receipt')}</p>
                  <p className="text-sm text-foreground-muted">{t('add_expense')}</p>
                </div>
                <ChevronRight size={20} className="text-foreground-muted" />
              </Link>
            </div>
          </section>

          {/* 3. Tímar í dag */}
          <section className="card p-6" data-testid="worker-summary">
            <h2 className="text-xs uppercase tracking-wide text-foreground-muted mb-4">
              {t('time_tracking')}
            </h2>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-foreground-muted text-sm mb-1">{t('today')}</p>
                <p className="text-4xl font-bold font-mono text-accent" data-testid="hours-today">
                  {formatHoursNum(workerData?.hoursToday)}
                  <span className="text-lg text-foreground-muted ml-1">{t('hours_unit')}</span>
                </p>
              </div>
              <div>
                <p className="text-foreground-muted text-sm mb-1">{t('this_week')}</p>
                <p className="text-4xl font-bold font-mono" data-testid="hours-week">
                  {formatHoursNum(workerData?.hoursThisWeek)}
                  <span className="text-lg text-foreground-muted ml-1">{t('hours_unit')}</span>
                </p>
              </div>
            </div>
          </section>

          {/* 4. Verkefni */}
          <section>
            <Link to="/projects" className="flex items-center gap-4 p-5 card card-interactive" data-testid="action-view-projects">
              <div className="w-12 h-12 bg-card-hover rounded-xl flex items-center justify-center border border-border">
                <FolderOpen size={24} className="text-foreground-secondary" />
              </div>
              <div className="flex-1">
                <p className="font-semibold">{t('projects')}</p>
                <p className="text-sm text-foreground-muted">{t('view_active_projects')}</p>
              </div>
              <ChevronRight size={20} className="text-foreground-muted" />
            </Link>
          </section>

          {/* 5. Aðvörun */}
          {(attention.pending_receipts > 0 || (attention.projects_missing_hours?.length || 0) > 0) && (
            <section data-testid="worker-alerts">
              <h2 className="text-xs uppercase tracking-wide text-foreground-muted mb-4">
                {t('needs_attention')}
              </h2>
              <div className="space-y-3">
                {attention.projects_missing_hours?.length > 0 && (
                  <Link 
                    to="/time"
                    className="flex items-center gap-4 p-4 bg-warning/5 border border-warning/20 rounded-lg"
                    data-testid="alert-missing-hours"
                  >
                    <Clock className="text-warning flex-shrink-0" size={20} />
                    <div className="flex-1">
                      <p className="font-medium">{t('missing_hours_3_days')}</p>
                      <p className="text-sm text-foreground-muted">
                        {attention.projects_missing_hours.map(p => p.name).join(', ')}
                      </p>
                    </div>
                    <ChevronRight size={18} className="text-foreground-muted" />
                  </Link>
                )}
                {attention.pending_receipts > 0 && (
                  <Link 
                    to="/receipts?status=pending"
                    className="flex items-center gap-4 p-4 bg-card border border-border rounded-lg"
                    data-testid="alert-pending-receipts"
                  >
                    <Receipt className="text-foreground-muted flex-shrink-0" size={20} />
                    <div className="flex-1">
                      <p className="font-medium">{attention.pending_receipts} {t('receipts_pending')}</p>
                      <p className="text-sm text-foreground-muted">{t('needs_review')}</p>
                    </div>
                    <ChevronRight size={18} className="text-foreground-muted" />
                  </Link>
                )}
              </div>
            </section>
          )}

        </div>
      </>
    );
  }

  // ========== MANAGER DASHBOARD (Tier 2: operational) ==========
  if (isManager) {
    return (
      <>
        <div className="space-y-8" data-testid="dashboard-manager">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                {greeting}, {firstName}
              </h1>
              <p className="text-foreground-muted text-sm mt-1">{getFormattedDate()}</p>
            </div>
            <button onClick={refresh} className="p-2 text-foreground-muted hover:text-foreground rounded-lg transition-colors" data-testid="refresh-btn-manager" title={t('refresh')}>
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>

          {/* PWA Install suggestion */}
          <PWAInstallFlow counts={activityCounts} />

          {/* Quick actions - always first after greeting */}
          <section data-testid="quick-actions">
            <h2 className="text-xs uppercase tracking-wide text-foreground-muted mb-4">
              {t('quick_actions')}
            </h2>
            <div className="space-y-3">
              <Link to="/time" className="flex items-center gap-4 p-5 card card-interactive" data-testid="action-log-hours">
                <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
                  <Clock size={24} className="text-background" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold">{t('log_hours')}</p>
                  <p className="text-sm text-foreground-muted">{t('log_work_hours')}</p>
                </div>
                <ChevronRight size={20} className="text-foreground-muted" />
              </Link>
              <Link to="/receipts/new" className="flex items-center gap-4 p-5 card card-interactive" data-testid="action-upload-receipt">
                <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
                  <Camera size={24} className="text-background" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold">{t('scan_receipt')}</p>
                  <p className="text-sm text-foreground-muted">{t('add_expense')}</p>
                </div>
                <ChevronRight size={20} className="text-foreground-muted" />
              </Link>
              <Link to="/projects" className="flex items-center gap-4 p-5 card card-interactive" data-testid="action-view-projects">
                <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
                  <FolderOpen size={24} className="text-background" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold">{t('projects')}</p>
                  <p className="text-sm text-foreground-muted">{t('view_and_manage')}</p>
                </div>
                <ChevronRight size={20} className="text-foreground-muted" />
              </Link>
            </div>
          </section>

          <MyJobsSection />

          {/* Operational overview */}
          <section data-testid="manager-overview">
            <h2 className="text-xs uppercase tracking-wide text-foreground-muted mb-4">
              {t('dashboard_this_month')}
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="card p-5" data-testid="stat-hours">
                <p className="text-foreground-muted text-sm mb-1">{t('hours_label')}</p>
                <p className="text-3xl font-semibold font-mono text-accent">
                  {formatKlst(thisMonth.total_hours)}
                </p>
              </div>
              <div className="card p-5" data-testid="stat-projects">
                <p className="text-foreground-muted text-sm mb-1">{t('active_projects_count')}</p>
                <p className="text-3xl font-semibold font-mono">
                  {projectCount || 0}
                </p>
              </div>
            </div>
          </section>

          {/* Attention items */}
          {hasAttentionItems && (
            <section data-testid="attention-required">
              <h2 className="text-xs uppercase tracking-wide text-foreground-muted mb-4">
                {t('needs_attention')}
              </h2>
              <div className="space-y-3">
                {(attention.pending_receipts || 0) > 0 && (
                  <Link 
                    to="/receipts?status=pending"
                    className="flex items-center gap-4 p-4 bg-warning/5 border border-warning/20 rounded-lg"
                    data-testid="alert-pending"
                  >
                    <Receipt className="text-warning flex-shrink-0" size={20} />
                    <div className="flex-1">
                      <p className="font-medium">{t('receipts_pending')}</p>
                      <p className="text-sm text-foreground-muted">{attention.pending_receipts} {t('receipts_pending_count')}</p>
                    </div>
                    <ChevronRight size={18} className="text-foreground-muted" />
                  </Link>
                )}
                {(attention.unassigned_receipts || 0) > 0 && (
                  <Link 
                    to="/receipts?filter=unassigned"
                    className="flex items-center gap-4 p-4 bg-warning/5 border border-warning/20 rounded-lg"
                    data-testid="alert-unassigned"
                  >
                    <Link2Off className="text-warning flex-shrink-0" size={20} />
                    <div className="flex-1">
                      <p className="font-medium">{t('unassigned_receipts')}</p>
                      <p className="text-sm text-foreground-muted">{t('assign_to_project')}</p>
                    </div>
                    <ChevronRight size={18} className="text-foreground-muted" />
                  </Link>
                )}
                {attention.projects_missing_hours?.length > 0 && (
                  <div className="p-4 bg-card border border-border rounded-lg">
                    <div className="flex items-start gap-4">
                      <Clock className="text-foreground-muted flex-shrink-0 mt-0.5" size={20} />
                      <div>
                        <p className="font-medium">{t('missing_hours_3_days')}</p>
                        <ul className="mt-2 space-y-1">
                          {attention.projects_missing_hours.map(p => (
                            <li key={p.project_id}>
                              <Link 
                                to={`/time?project=${p.project_id}`}
                                className="text-sm text-foreground-secondary hover:text-accent"
                              >
                                - {p.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Upcoming projects notification */}
          {data?.upcoming_projects?.length > 0 && (
            <section data-testid="upcoming-projects">
              <h2 className="text-xs uppercase tracking-wide text-foreground-muted mb-4">
                {t('dashboard.upcoming_projects')}
              </h2>
              <div className="space-y-2">
                {data.upcoming_projects.map(p => {
                  const daysUntil = Math.ceil((new Date(p.planned_start_date) - new Date(data.today)) / 86400000);
                  const label = daysUntil === 0 ? t('dashboard.starts_today') 
                    : daysUntil === 1 ? t('dashboard.starts_tomorrow')
                    : t('dashboard.starts_in_days').replace('{days}', daysUntil);
                  return (
                    <Link 
                      key={p.project_id}
                      to={`/projects/${p.project_id}`}
                      className="flex items-center gap-4 p-4 bg-accent/5 border border-accent/20 rounded-lg"
                      data-testid={`upcoming-${p.project_id}`}
                    >
                      <CalendarDays className="text-accent flex-shrink-0" size={20} />
                      <div className="flex-1">
                        <p className="font-medium">{p.name}</p>
                        <p className="text-sm text-foreground-muted">{label}</p>
                      </div>
                      <ChevronRight size={18} className="text-foreground-muted" />
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* Team hours */}
          {teamHours.length > 0 && (
            <section data-testid="team-hours">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xs uppercase tracking-wide text-foreground-muted">
                  <Users size={14} className="inline mr-1.5 -mt-0.5" />
                  {t('team')}
                </h2>
                <div className="flex gap-1 bg-card rounded-lg p-0.5 border border-border" data-testid="team-toggle">
                  {teamToggleItems.map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setTeamView(key)}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                        teamView === key
                          ? 'bg-accent text-background'
                          : 'text-foreground-muted hover:text-foreground'
                      }`}
                      data-testid={`team-toggle-${key}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="card divide-y divide-border">
                {teamHours.map((member) => {
                  const hours = teamView === 'today' ? member.hours_today
                    : teamView === 'week' ? member.hours_week
                    : member.hours_month;
                  return (
                    <div key={member.user_id} className="flex items-center justify-between p-4" data-testid={`team-member-${member.user_id}`}>
                      <span className="text-sm font-medium">{member.name}</span>
                      <span className={`font-mono text-sm ${hours > 0 ? 'text-accent' : 'text-foreground-muted'}`}>
                        {hours} {t('hours_unit')}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

        </div>
      </>
    );
  }

  // ========== OWNER/ADMIN/BOOKKEEPER DASHBOARD (Tier 1: full financial) ==========
  return (
    <>
      <div className="space-y-8" data-testid="dashboard">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {greeting}, {firstName}
            </h1>
            <p className="text-foreground-muted text-sm mt-1">{getFormattedDate()}</p>
          </div>
          <button onClick={refresh} className="p-2 text-foreground-muted hover:text-foreground rounded-lg transition-colors" data-testid="refresh-btn-admin" title={t('refresh')}>
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* ========== EMPTY STATE ========== */}
        {showOnboardingGuide && !hasAttentionItems && (
          <section className="card p-6 border-2 border-dashed border-border" data-testid="workflow-guide">
            <h2 className="font-semibold mb-4">{t('how_rektra_works')}</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 text-accent font-bold text-sm">1</div>
                <div>
                  <p className="font-medium">{t('step1_title')}</p>
                  <p className="text-sm text-foreground-muted">{t('step1_desc')}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 text-accent font-bold text-sm">2</div>
                <div>
                  <p className="font-medium">{t('step2_title')}</p>
                  <p className="text-sm text-foreground-muted">{t('step2_desc')}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 text-accent font-bold text-sm">3</div>
                <div>
                  <p className="font-medium">{t('step3_title')}</p>
                  <p className="text-sm text-foreground-muted">{t('step3_desc')}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 text-accent font-bold text-sm">4</div>
                <div>
                  <p className="font-medium">{t('step4_title')}</p>
                  <p className="text-sm text-foreground-muted">{t('step4_desc')}</p>
                </div>
              </div>
            </div>
            <Link 
              to="/receipts/new" 
              className="btn btn-primary mt-6 w-full"
              data-testid="start-scanning-btn"
            >
              <Camera size={18} />
              {t('start_scanning')}
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/projects/example"
              onClick={() => sessionStorage.setItem('example_from', '/dashboard')}
              className="block text-center text-sm text-foreground-muted hover:text-accent transition-colors mt-3 py-2"
              data-testid="dashboard-view-example"
            >
              {t('example_view_link')}
            </Link>
          </section>
        )}

        {/* Fyrstu skrefin: owner checklist that ticks itself from real data. */}
        {user?.role === 'owner' && (
          <FirstStepsCard
            counts={activityCounts}
            teamMembers={planCtx?.currentUsers || 0}
            firstProjectId={cached?.firstProjectId || null}
          />
        )}

        {/* Invite the team: only once a project exists (iter323, moved out
            of the welcome intro where it was the biggest drop-off step). */}
        {projectCount > 0 && ['owner', 'admin'].includes(user?.role) && <InviteTeamCard />}

        {/* PWA Install suggestion */}
        <PWAInstallFlow counts={activityCounts} />

        {/* ========== QUICK ACTIONS (Actions first, information second) ========== */}
        <section data-testid="quick-actions">
          <h2 className="text-xs uppercase tracking-wide text-foreground-muted mb-4">
            {t('quick_actions')}
          </h2>
          <div className="space-y-3">
            {isBookkeeper ? (
              <>
                <Link to="/reports/hours" className="flex items-center gap-4 p-5 card card-interactive" data-testid="action-hours-overview">
                  <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
                    <Clock size={24} className="text-background" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{t('hours_overview')}</p>
                    <p className="text-sm text-foreground-muted">{t('hours_overview_subtitle')}</p>
                  </div>
                  <ChevronRight size={20} className="text-foreground-muted" />
                </Link>
                <Link to="/receipts" className="flex items-center gap-4 p-5 card card-interactive" data-testid="action-view-receipts">
                  <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
                    <Camera size={24} className="text-background" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{t('receipts')}</p>
                    <p className="text-sm text-foreground-muted">{t('view_receipts')}</p>
                  </div>
                  <ChevronRight size={20} className="text-foreground-muted" />
                </Link>
              </>
            ) : (
              <>
                <Link to="/time" className="flex items-center gap-4 p-5 card card-interactive" data-testid="action-log-hours">
                  <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
                    <Clock size={24} className="text-background" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{t('log_hours')}</p>
                    <p className="text-sm text-foreground-muted">{t('log_work_hours')}</p>
                  </div>
                  <ChevronRight size={20} className="text-foreground-muted" />
                </Link>
                <Link to="/receipts/new" className="flex items-center gap-4 p-5 card card-interactive" data-testid="action-upload-receipt">
                  <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
                    <Camera size={24} className="text-background" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{t('scan_receipt')}</p>
                    <p className="text-sm text-foreground-muted">{t('add_expense')}</p>
                  </div>
                  <ChevronRight size={20} className="text-foreground-muted" />
                </Link>
              </>
            )}
            <Link to="/projects" className="flex items-center gap-4 p-5 card card-interactive" data-testid="action-view-projects">
              <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
                <FolderOpen size={24} className="text-background" />
              </div>
              <div className="flex-1">
                <p className="font-semibold">{t('projects')}</p>
                <p className="text-sm text-foreground-muted">{t('view_and_manage')}</p>
              </div>
              <ChevronRight size={20} className="text-foreground-muted" />
            </Link>
          </div>
        </section>

        {/* ========== TIL REIKNINGSFÆRSLU (own card, right after the actions) ========== */}
        <section data-testid="ready-to-bill">
          <div className="card overflow-hidden" data-testid="action-billing-card">
            <Link to="/billing" className="flex items-center gap-4 p-5 card-interactive border-b border-border" data-testid="action-billing-dashboard">
              <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
                <FileText size={24} className="text-background" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold">{lang === 'is' ? 'Til reikningsfærslu' : 'Ready to Bill'}</p>
                <div className="mt-0.5">
                  <UnbilledTotalBadge lang={lang} />
                </div>
              </div>
              <ChevronRight size={20} className="text-foreground-muted flex-shrink-0" />
            </Link>
            <button
              onClick={() => setPickerOpen(true)}
              className="w-full px-5 py-3 text-sm font-medium text-accent hover:bg-accent/5 transition-colors flex items-center justify-center gap-2"
              data-testid="dashboard-create-invoice-btn"
            >
              <FileText size={14} />
              {createInvoiceLabel}
              <ArrowRight size={14} />
            </button>
          </div>
        </section>

        {/* ========== OPERATIONAL ALERTS ========== */}
        {hasAttentionItems && (
          <section data-testid="attention-required">
            <h2 className="text-xs uppercase tracking-wide text-foreground-muted mb-4">
              {t('needs_attention')}
            </h2>
            <div className="space-y-3">
              {attention.pending_receipts > 0 && (
                <Link 
                  to="/receipts?status=pending"
                  className="flex items-center gap-4 p-4 bg-warning/5 border border-warning/20 rounded-lg"
                  data-testid="alert-pending"
                >
                  <Receipt className="text-warning flex-shrink-0" size={20} />
                  <div className="flex-1">
                    <p className="font-medium">{t('receipts_pending')}</p>
                    <p className="text-sm text-foreground-muted">{attention.pending_receipts} {t('receipts_pending_count')}</p>
                  </div>
                  <ChevronRight size={18} className="text-foreground-muted" />
                </Link>
              )}
              {attention.unassigned_receipts > 0 && (
                <Link 
                  to="/receipts?filter=unassigned"
                  className="flex items-center gap-4 p-4 bg-warning/5 border border-warning/20 rounded-lg"
                  data-testid="alert-unassigned"
                >
                  <Link2Off className="text-warning flex-shrink-0" size={20} />
                  <div className="flex-1">
                    <p className="font-medium">{t('unassigned_receipts')}</p>
                    <p className="text-sm text-foreground-muted">{t('assign_to_project')}</p>
                  </div>
                  <ChevronRight size={18} className="text-foreground-muted" />
                </Link>
              )}
              {attention.projects_missing_hours?.length > 0 && (
                <div className="p-4 bg-card border border-border rounded-lg">
                  <div className="flex items-start gap-4">
                    <Clock className="text-foreground-muted flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <p className="font-medium">{t('missing_hours_3_days')}</p>
                      <ul className="mt-2 space-y-1">
                        {attention.projects_missing_hours.map(p => (
                          <li key={p.project_id}>
                            <Link 
                              to={`/time?project=${p.project_id}`}
                              className="text-sm text-foreground-secondary hover:text-accent"
                            >
                              - {p.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ========== MY TASKS (All roles) ========== */}
        <MyJobsSection />

        {/* ========== MONTH SELECTOR + FINANCIAL OVERVIEW ========== */}
        <section data-testid="this-month-overview">
            {/* Month selector */}
            <div className="flex items-center justify-between mb-4">
              <button onClick={prevMonth} className="p-2 hover:bg-card-hover rounded-lg transition-colors" data-testid="dashboard-prev-month">
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => setShowMonthPicker(true)}
                className="flex items-center gap-2 text-sm font-medium hover:text-accent transition-colors"
                data-testid="month-label-tap"
              >
                <CalendarDays size={16} className="text-foreground-muted" />
                <span className="capitalize">{monthNames[selMonth - 1]} {selYear}</span>
              </button>
              <button
                onClick={nextMonth}
                disabled={isCurrentMonth}
                className={`p-2 rounded-lg transition-colors ${isCurrentMonth ? 'opacity-30 cursor-not-allowed' : 'hover:bg-card-hover'}`}
                data-testid="dashboard-next-month"
              >
                <ChevronRight size={20} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Allur kostnaður */}
              <div className="card p-5" data-testid="stat-allur-kostnadur">
                <p className="text-foreground-muted text-sm mb-1 flex items-center gap-1">
                  {t('dashboard_allur_kostnadur')}
                  <InfoTooltip text={t('dashboard_allur_tooltip')} />
                </p>
                <p className="text-3xl font-semibold font-mono text-accent">
                  {formatCurrency(thisMonth.allur_kostnadur)} <span className="text-base text-foreground-muted">{t('kr')}</span>
                </p>
              </div>

              {/* Rekstrarkostnaður (án VSK) */}
              <div className="card p-5" data-testid="stat-rekstrarkostnadur">
                <p className="text-foreground-muted text-sm mb-1 flex items-center gap-1">
                  {t('dashboard_rekstrar')}
                  <InfoTooltip text={t('dashboard_rekstrar_tooltip')} />
                </p>
                <p className="text-3xl font-semibold font-mono">
                  {formatCurrency(thisMonth.rekstrarkostnadur_net)} <span className="text-base text-foreground-muted">{t('kr')}</span>
                </p>
              </div>

              {/* Innskattur VSK */}
              <div className="card p-5" data-testid="stat-innskattur-vsk">
                <p className="text-foreground-muted text-sm mb-1 flex items-center gap-1">
                  {t('dashboard_innskattur')}
                  <InfoTooltip text={t('dashboard_innskattur_tooltip')} />
                </p>
                <p className="text-3xl font-semibold font-mono">
                  {formatCurrency(thisMonth.innskattur_vsk)} <span className="text-base text-foreground-muted">{t('kr')}</span>
                </p>
              </div>
            </div>

            {/* Hours row */}
            <div className="mt-4 card p-4 flex items-center justify-between" data-testid="stat-hours">
              <p className="text-foreground-muted text-sm flex items-center gap-1.5">
                <Clock size={14} /> {t('dashboard_hours_label')}
              </p>
              <p className="text-lg font-semibold font-mono text-accent">
                {formatHoursNum(thisMonth.total_hours)} <span className="text-sm text-foreground-muted">{t('hours_unit')}</span>
              </p>
            </div>
          </section>

        {/* ========== VAT SUMMARY (Owner/Admin/Bookkeeper) ========== */}
        {isFullFinancial && <VatSummaryCard selectedMonth={selectedMonthKey} />}


        {/* ========== TEAM HOURS (Owner/Admin only) ========== */}
        {isFullFinancial && !isBookkeeper && teamHours.length > 0 && (
          <section data-testid="team-hours">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xs uppercase tracking-wide text-foreground-muted">
                <Users size={14} className="inline mr-1.5 -mt-0.5" />
                {t('team')}
              </h2>
              <div className="flex gap-1 bg-card rounded-lg p-0.5 border border-border" data-testid="team-toggle">
                {teamToggleItems.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setTeamView(key)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      teamView === key
                        ? 'bg-accent text-background'
                        : 'text-foreground-muted hover:text-foreground'
                    }`}
                    data-testid={`team-toggle-${key}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="card divide-y divide-border">
              {teamHours.map((member) => {
                const hours = teamView === 'today' ? member.hours_today
                  : teamView === 'week' ? member.hours_week
                  : member.hours_month;
                return (
                  <div key={member.user_id} className="flex items-center justify-between p-4" data-testid={`team-member-${member.user_id}`}>
                    <span className="text-sm font-medium">{member.name}</span>
                    <span className={`font-mono text-sm ${hours > 0 ? 'text-accent' : 'text-foreground-muted'}`}>
                      {hours} {t('hours_unit')}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

      </div>

      {showMonthPicker && (
        <MonthPicker
          value={selectedMonthKey}
          onChange={(val) => {
            const [y, m] = val.split('-').map(Number);
            setSelYear(y);
            setSelMonth(m);
          }}
          onClose={() => setShowMonthPicker(false)}
        />
      )}

      {/* Invoice creation flow — pick a project, then open the editor */}
      <InvoiceProjectPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(p) => { setPickerOpen(false); setEditorProjectId(p.project_id); }}
      />
      <InvoiceEditorModal
        open={!!editorProjectId}
        onClose={() => setEditorProjectId(null)}
        projectId={editorProjectId}
        onSent={() => setEditorProjectId(null)}
      />
    </>
  );
};

export default Dashboard;
